import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BarChart3, Eye, MousePointerClick } from 'lucide-react';
import { format, subDays } from 'date-fns';

/**
 * Performance report for Mid-Search (placement = 'middle_banner_search')
 * project banners. Aggregates ad_events impressions/clicks per linked
 * project within a user-selectable date range.
 */
export function MidSearchBannerReport() {
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  const { data: campaigns } = useQuery({
    queryKey: ['mid-search-campaigns-for-report'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('ad_campaigns')
        .select('id, title, linked_project_id, projects:linked_project_id (id, name)')
        .eq('placement', 'middle_banner_search');
      if (error) throw error;
      return data || [];
    },
  });

  const campaignIds = useMemo(() => (campaigns || []).map((c: any) => c.id), [campaigns]);

  const { data: events, isLoading } = useQuery({
    queryKey: ['mid-search-events', startDate, endDate, campaignIds.join(',')],
    enabled: campaignIds.length > 0,
    queryFn: async () => {
      const startISO = new Date(startDate + 'T00:00:00').toISOString();
      const endISO = new Date(endDate + 'T23:59:59').toISOString();
      const { data, error } = await (supabase as any)
        .from('ad_events')
        .select('campaign_id, event_type')
        .in('campaign_id', campaignIds)
        .eq('placement', 'middle_banner_search')
        .gte('created_at', startISO)
        .lte('created_at', endISO);
      if (error) throw error;
      return data || [];
    },
  });

  const rows = useMemo(() => {
    const tally = new Map<string, { impressions: number; clicks: number }>();
    (events || []).forEach((e: any) => {
      const t = tally.get(e.campaign_id) || { impressions: 0, clicks: 0 };
      if (e.event_type === 'impression') t.impressions += 1;
      else if (e.event_type === 'click') t.clicks += 1;
      tally.set(e.campaign_id, t);
    });
    return (campaigns || [])
      .map((c: any) => {
        const t = tally.get(c.id) || { impressions: 0, clicks: 0 };
        const ctr = t.impressions > 0 ? (t.clicks / t.impressions) * 100 : 0;
        return {
          id: c.id,
          projectName: c.projects?.name || '— (project unlinked)',
          title: c.title,
          impressions: t.impressions,
          clicks: t.clicks,
          ctr,
        };
      })
      .sort((a: any, b: any) => b.impressions - a.impressions);
  }, [events, campaigns]);

  const totals = useMemo(
    () => rows.reduce(
      (acc, r) => ({ impressions: acc.impressions + r.impressions, clicks: acc.clicks + r.clicks }),
      { impressions: 0, clicks: 0 }
    ),
    [rows]
  );
  const totalCtr = totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-primary" /> Mid-Search Banner Performance
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Impressions and clicks per project banner shown in search-result feeds.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Start date</Label>
            <Input type="date" value={startDate} max={endDate} onChange={(e) => setStartDate(e.target.value)} className="h-9 w-auto" />
          </div>
          <div>
            <Label className="text-xs">End date</Label>
            <Input type="date" value={endDate} min={startDate} max={format(new Date(), 'yyyy-MM-dd')} onChange={(e) => setEndDate(e.target.value)} className="h-9 w-auto" />
          </div>
          <div className="flex gap-2 ml-auto">
            <div className="rounded border px-3 py-1.5 text-xs flex items-center gap-1.5">
              <Eye className="h-3.5 w-3.5 text-purple-500" />
              <span className="font-semibold">{totals.impressions.toLocaleString()}</span> impressions
            </div>
            <div className="rounded border px-3 py-1.5 text-xs flex items-center gap-1.5">
              <MousePointerClick className="h-3.5 w-3.5 text-pink-500" />
              <span className="font-semibold">{totals.clicks.toLocaleString()}</span> clicks
            </div>
            <div className="rounded border px-3 py-1.5 text-xs">
              CTR <span className="font-semibold">{totalCtr.toFixed(2)}%</span>
            </div>
          </div>
        </div>

        {isLoading ? (
          <Skeleton className="h-32" />
        ) : !rows.length ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            No mid-search banners yet. Create one from Ad Campaigns to start collecting data.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Project</TableHead>
                  <TableHead>Banner</TableHead>
                  <TableHead className="text-right">Impressions</TableHead>
                  <TableHead className="text-right">Clicks</TableHead>
                  <TableHead className="text-right">CTR</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.projectName}</TableCell>
                    <TableCell className="text-xs text-muted-foreground truncate max-w-[260px]">{r.title}</TableCell>
                    <TableCell className="text-right">{r.impressions.toLocaleString()}</TableCell>
                    <TableCell className="text-right">{r.clicks.toLocaleString()}</TableCell>
                    <TableCell className="text-right">{r.ctr.toFixed(2)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
